/******/ (() => { // webpackBootstrap

class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.daysDigits = new Array(this.maxDays.length).fill(0);
    this.lastDigits = [...this.daysDigits, 0, 0, 0, 0, 0, 0];
    this.digitContainers = this.querySelectorAll('[data-digit]');
    this.wethemeGlobal = document.querySelector('script#wetheme-global');
    this.translationsObject = JSON.parse(this.wethemeGlobal.textContent);
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // If time is invalid or negative, handle it, but allow reset logic to proceed
    if (this.timeRemaining > this.timeRemainingMax) return;

    // Clear any existing interval to prevent duplicates
    if (this.timerInterval) clearInterval(this.timerInterval);

    this.timerInterval = setInterval(() => {
      if (this.timeRemaining > 0) {
        this.updateTimer();
      } else {
        clearInterval(this.timerInterval);
        // Ensure we trigger complete logic immediately when hitting 0
        this.updateTimer(); 
      }
    }, 1000);
    
    // Immediate update to set initial state
    this.updateTimer();
  }

  updateTimer() {
    const timeRemaining = Math.max(0, this.timeRemaining); // Prevent negative numbers
    const timeValues = this.getTimeValues(timeRemaining);
    const nowDigits = this.getDigitsFromTimeValues(timeValues);

    // If time digit has changed, animate in the new digit
    nowDigits.map((nowDigit, index) => {
      const lastDigit = this.lastDigits[index]
      if (nowDigit != lastDigit) {
        this.animateDigit(this.digitContainers[index], nowDigit);
      }
    });

    this.lastDigits = nowDigits;
    this.setAttribute('aria-label', `${timeValues.days} ${this.translationsObject.translations.days} ${timeValues.hours} ${this.translationsObject.translations.hours} ${timeValues.minutes} ${this.translationsObject.translations.minutes} ${timeValues.seconds} ${this.translationsObject.translations.seconds} remaining`);

    // Check completion
    this.handleTimerComplete(nowDigits);
  }

 resetTimer() {
    const now = new Date();
    
    // 1. Get the original target date from the data attributes
    // We construct it manually to ensure we have the starting point
    const originalTarget = new Date(`${this.year}/${this.month}/${this.day} ${this.hour}:${this.minute}:${this.second} ${this.timezone}`);
    
    const intervalMs = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    const timePassedSinceEnd = now.getTime() - originalTarget.getTime();
    
    // 2. Calculate how many full 7-day intervals have passed since the original date
    // and add one more interval to get the NEXT expiration date.
    const intervalsPassed = Math.floor(timePassedSinceEnd / intervalMs);
    const nextTargetDate = new Date(originalTarget.getTime() + (intervalsPassed + 1) * intervalMs);

    // 3. Format the nextTargetDate relative to Sydney for the attributes
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Australia/Sydney',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
      timeZoneName: 'shortOffset' 
    });

    const parts = formatter.formatToParts(nextTargetDate);
    const getPart = (type) => parts.find(p => p.type === type)?.value;

    // 4. Update the dataset attributes with the calculated future "cycle" date
    this.dataset.year = getPart('year');
    this.dataset.month = getPart('month');
    this.dataset.day = getPart('day');
    this.dataset.hour = getPart('hour');
    this.dataset.minute = getPart('minute');
    this.dataset.second = getPart('second');
    this.dataset.timezone = getPart('timeZoneName'); 

    // UI Clean up
    this.classList.remove('hide');
    const timerCompleteContent = document.querySelectorAll('[data-timer-complete-content]');
    if (timerCompleteContent) {
      timerCompleteContent.forEach(content => {
        if (content.dataset.timerId === this.timerId) {
          content.classList.add('hide');
        }
      });
    }

    this.init(); 
}

  animateDigit(container, now) {
    // Reset after animation
    setTimeout(() => {
      // Update visible element with the time now
      const last_digit = container.querySelector('[data-digit-last]');
      if(last_digit) last_digit.innerHTML = now;
    }, 500)

    this.hideWhenZero(container, now);
  }

  // Takes an object with days, hours, minutes and seconds, returns an array of 9 digits
  getDigitsFromTimeValues(timeValues) {
    return [
      ...timeValues.days.toString().padStart(this.maxDays.length, '0').split('').map(Number),
      ...timeValues.hours.toString().padStart(2, '0').split('').map(Number),
      ...timeValues.minutes.toString().padStart(2, '0').split('').map(Number),
      ...timeValues.seconds.toString().padStart(2, '0').split('').map(Number)
    ];
  }

  // Takes a time in milliseconds, returns an object with days, hours, minutes and seconds
  getTimeValues(time) {
    return {
      days: Math.floor(time / (1000 * 60 * 60 * 24)),
      hours: Math.floor((time / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((time / 1000 / 60) % 60),
      seconds: Math.floor((time / 1000) % 60)
    };
  }

  hideWhenZero(element, digit) {
    if (!element) return;
    if (element.hasAttribute('data-hide-when-zero') && digit === 0) element.parentElement.classList.add('hide');
  }

  handleTimerComplete(nowDigits) {
    const isComplete = !nowDigits.filter(digit => digit !== 0).length;
    
    // Only proceed if complete
    if (!isComplete) return;

    // Optional: Briefly show 00:00:00 or "Expired" content before resetting
    // If you want it to reset INSTANTLY without showing 00:00:00, move resetTimer to the top of this function.
    
    // Reset the timer to 7 days
    this.resetTimer();
  }

  hideTimer() {
    this.classList.add('hide');
  }

  handleTimerCompleteContent() {
    this.showHideTimerCompleteContent();
    this.disableExpiredLinks();
  }

  showHideTimerCompleteContent() {
    // Show/hide content when the timer is complete
    const timerCompleteContent = document.querySelectorAll('[data-timer-complete-content]');
    if (!timerCompleteContent) return;
    timerCompleteContent.forEach(content => {
      if (content.dataset.timerId === this.timerId) {
        content.classList.toggle('hide');
      }
    });
  }

  disableExpiredLinks() {
    // Disable links that expire once the timer is complete
    const timerCompleteLinks = document.querySelectorAll('[data-timer-link]');
    if (!timerCompleteLinks) return;
    timerCompleteLinks.forEach(link => {
      if (link.dataset.timerId === this.timerId) {
        link.removeAttribute('href');
        link.setAttribute('role', 'link');
        link.setAttribute('aria-disabled', 'true');
      }
    })
  }

  get timeRemaining() {
    return +new Date(`${this.year}/${this.month}/${this.day} ${this.hour}:${this.minute}:${this.second} ${this.timezone}`).getTime() - +new Date().getTime();
  }

  get timeRemainingMax() {
    return Infinity; 
  }

  get maxDays() {
    return this.dataset.maxDays || '00'; // Added fallback
  }

  get hideWhenComplete() {
    return this.dataset.hideWhenComplete === 'true';
  }

  get timezone() {
    return this.dataset.timezone || '';
  }

  get year() {
    return this.dataset.year;
  }

  get month() {
    return this.dataset.month;
  }

  get day() {
    return this.dataset.day;
  }

  get hour() {
    return this.dataset.hour;
  }

  get minute() {
    return this.dataset.minute;
  }

  get second() {
    return this.dataset.second;
  }

  get timerId() {
    return this.dataset.timerId;
  }

  disconnectedCallback() {
    clearInterval(this.timerInterval);
  }
}

customElements.define('countdown-timer', CountdownTimer);

/******/ })()
;